// Agents list view. fn-1.29.
//
// **Acceptance**: "Agents list shows the user's accessible agents;
// click → 'Start run' creates a session and navigates to `/runs/:id`."
//
// **Wiring**:
//   - Source: `trpc.agents.list.useQuery()` (BFF proxy over `GET /agents`).
//   - Start-run: `trpc.runs.start.useMutation()` (BFF proxy over
//     `POST /sessions`). On success, navigate to `/runs/<id>`.
//   - `matterId` for `runs.start` is provided by a text input on each
//     row (mandatory at v1; the agent ALWAYS runs in a matter context).
//     A matter picker is a future enhancement; the input keeps the
//     surface dogfoodable without a matter directory.

import type { BffRouter } from '@sb-ai-platform/bff';
import { keepPreviousData } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import { Fragment, useCallback, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { SbIcon } from '../../design/sb-icon.js';
import { useClientOtel, useTrackUserAction } from '../../instrumentation/provider.js';
import { useFavorites } from '../../prefs/index.js';
import { trpc } from '../../trpc-client.js';
import { useDebouncedTextFilter } from '../config-list-text-filter.js';
import { type AgentMemoryStatus, agentsEmptyState, buildAgentEntries } from './merge.js';
import { useIsPlatformAdmin } from './use-is-platform-admin.js';

// ADR 0043 — the agents-list memory badge. Memory is agent-defined; each row
// shows the declared scopes + access mode (e.g. `User:RW Firm:RO`). Read-only /
// informational — the map is authored in the config repo, not here.
const MEMORY_STATUS_SCOPE_ORDER = ['user', 'firm', 'matter'] as const;
const MEMORY_STATUS_SCOPE_ABBR: Record<(typeof MEMORY_STATUS_SCOPE_ORDER)[number], string> = {
  user: 'User',
  firm: 'Firm',
  matter: 'Matter',
};
const MEMORY_STATUS_ACCESS_ABBR: Record<'read_only' | 'read_write', string> = {
  read_only: 'RO',
  read_write: 'RW',
};

/**
 * Read-only memory-status badge for an agents-list row (ADR 0043). Renders the
 * agent's declared memory scopes + access mode (`User:RW Firm:RO Matter:RW`),
 * canonical order. Returns null when the agent declares no memory (opt-in).
 */
export function MemoryStatusBadge(props: {
  readonly memory?: AgentMemoryStatus | undefined;
}): React.ReactElement | null {
  const memory = props.memory;
  if (memory === undefined) return null;
  const scopes = MEMORY_STATUS_SCOPE_ORDER.filter((scope) => memory[scope] !== undefined);
  if (scopes.length === 0) return null;
  return (
    <span
      className="caption agents-list__memory"
      title="Memory this agent attaches (defined by the agent, not per session)"
    >
      Memory:{' '}
      {scopes.map((scope, index) => (
        <Fragment key={scope}>
          {index > 0 && ' '}
          <span className="agents-list__memory-item">
            {MEMORY_STATUS_SCOPE_ABBR[scope]}:
            {MEMORY_STATUS_ACCESS_ABBR[memory[scope] as 'read_only' | 'read_write']}
          </span>
        </Fragment>
      ))}
    </span>
  );
}

// Page-specific styling (design system 1a — dense, scan-first table). The
// design-system.css primitives (.table/.filters/.tag/.btn/…) are loaded
// globally via main.tsx; this file adds only the page-namespaced glue.
import './agents-list.css';

// The config-repo-agent row/status shapes are owned by the control plane
// and surfaced through the BFF's `configRepoAgents.list` procedure. We
// derive them from the router output rather than re-declaring them, so a
// contract change is a compile error in this file instead of a silent
// runtime drift behind a cast.
type ConfigRepoAgentsListOutput = inferRouterOutputs<BffRouter>['configRepoAgents']['list'];
export type ConfigRepoAgentRow = ConfigRepoAgentsListOutput['data'][number];
export type ConfigRepoAgentStatus = ConfigRepoAgentRow['status'];

// ADR 0043 — anchor `merge.ts`'s local `AgentMemoryStatus` to the BFF wire
// contract. `agents.list` rows are passed verbatim as `spineRows` into
// `buildAgentEntries`, so the spine's `memory` type IS the `agents.list` output
// row's `memory` type. `merge.ts` stays a pure, dependency-free module (it can't
// import the tRPC router without a cycle), but this bidirectional assignment
// makes any drift between its local shape and the `GET /agents` `agent.memory`
// echo a COMPILE error here rather than a silent runtime mismatch.
type AgentsListRow = inferRouterOutputs<BffRouter>['agents']['list']['data'][number];
type SpineMemoryStatus = NonNullable<AgentsListRow['memory']>;
// `AssertTrue<T extends true>` fails to compile when `T` is `false` — the
// constraint rejects it. Unlike a bare `? true : never` (a `never` tuple
// element is legal and would NOT error), this genuinely turns a shape mismatch
// into a compile error at the declaration below.
type AssertTrue<T extends true> = T;
type _AssertMemoryStatusMatchesWire = [
  AssertTrue<AgentMemoryStatus extends SpineMemoryStatus ? true : false>,
  AssertTrue<SpineMemoryStatus extends AgentMemoryStatus ? true : false>,
];

export type ConfigRepoAgentSkillRow = ConfigRepoAgentRow['skills'][number];
export type ConfigRepoAgentMcpRow = ConfigRepoAgentRow['mcp_servers'][number];
export type ConfigRepoAgentSkillStatus = ConfigRepoAgentSkillRow['status'];
export type ConfigRepoAgentMcpStatus = ConfigRepoAgentMcpRow['status'];
type ConfigRepoAgentSortBy =
  | 'name'
  | 'status'
  | 'updated_at'
  | 'scanned_at'
  | 'deployed_at'
  | 'model_id';
type SortDirection = 'asc' | 'desc';

export interface ConfigRepoAgentsListInput {
  readonly limit: number;
  readonly q?: string;
  readonly sort_by: ConfigRepoAgentSortBy;
  readonly sort_dir: SortDirection;
  readonly cursor?: string;
  readonly status?: ConfigRepoAgentStatus;
  readonly model_id?: string;
  readonly agent_ma_id?: string;
  readonly skill_name?: string;
  readonly mcp_server_name?: string;
}

export type ConfigRepoAgentWithSkills = Pick<ConfigRepoAgentRow, 'skills'>;
export type ConfigRepoAgentWithMcpServers = Pick<ConfigRepoAgentRow, 'mcp_servers'>;

export interface DependencyErrorDetailsShape {
  readonly skills?: {
    readonly missing?: readonly string[];
    readonly unpublished?: readonly string[];
  };
  readonly mcp_servers?: {
    readonly missing?: readonly string[];
    readonly invalid?: readonly string[];
  };
  readonly tools?: {
    readonly unknown_mcp_server_names?: readonly string[];
  };
}

const PAGE_SIZES = [10, 25, 50, 100] as const;
const CONFIG_REPO_AGENT_SORT_OPTIONS: ReadonlyArray<{
  readonly value: ConfigRepoAgentSortBy;
  readonly label: string;
}> = [
  { value: 'name', label: 'Name' },
  { value: 'status', label: 'Status' },
  { value: 'updated_at', label: 'Updated' },
  { value: 'scanned_at', label: 'Scanned' },
  { value: 'deployed_at', label: 'Deployed' },
  { value: 'model_id', label: 'Model' },
];
const CONFIG_REPO_AGENT_STATUS_OPTIONS: ReadonlyArray<{
  readonly value: ConfigRepoAgentStatus;
  readonly label: string;
}> = [
  { value: 'new', label: 'New' },
  { value: 'changed', label: 'Changed' },
  { value: 'unpublished', label: 'Unpublished' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'in_sync', label: 'In sync' },
  { value: 'error', label: 'Error' },
];

export function formatConfigRepoAgentStatus(status: ConfigRepoAgentStatus): string {
  switch (status) {
    case 'new':
      return 'New';
    case 'in_sync':
      return 'In sync';
    case 'changed':
      return 'Changed';
    case 'unpublished':
      return 'Unpublished';
    case 'deleted':
      return 'Deleted';
    case 'error':
      return 'Error';
  }
}

// A resolved status tag for a row: which design-system `.tag` variant to use,
// its label, and whether it is the error case (rendered with the inline danger
// colours — the design system has no `tag--danger` variant).
export interface AgentStatusTag {
  readonly label: string;
  readonly className: string;
  readonly danger: boolean;
}

// Map a config-repo agent status onto a design-system status tag (console
// redesign, layout 1a). deployed / in-sync → green; changed / out-of-sync →
// yellow; new / unpublished / deleted (draft-like) → grey; error → the inline
// danger tag. Pure + exported so the mapping is unit-tested independently of the
// row markup.
export function configRepoAgentStatusTag(status: ConfigRepoAgentStatus): AgentStatusTag {
  switch (status) {
    case 'in_sync':
      return { label: 'In sync', className: 'tag tag--green', danger: false };
    case 'changed':
      return { label: 'Changed', className: 'tag tag--yellow', danger: false };
    case 'new':
      return { label: 'New', className: 'tag tag--grey', danger: false };
    case 'unpublished':
      return { label: 'Unpublished', className: 'tag tag--grey', danger: false };
    case 'deleted':
      return { label: 'Deleted', className: 'tag tag--grey', danger: false };
    case 'error':
      return { label: 'Error', className: 'tag', danger: true };
  }
}

// The inline danger palette for the error tag (the design system exposes no
// `tag--danger` class, so the error case is coloured inline per the mock).
const DANGER_TAG_STYLE = {
  background: 'var(--color-background-danger-subtle)',
  color: 'var(--color-text-danger)',
} as const;

// A deployed spine row (console-authored or config-repo fallback) is always
// live — surface it as a green "Deployed" tag, or grey "Archived" when the
// console agent is soft-archived.
function deployedStatusTag(archived: boolean): AgentStatusTag {
  return archived
    ? { label: 'Archived', className: 'tag tag--grey', danger: false }
    : { label: 'Deployed', className: 'tag tag--green', danger: false };
}

function StatusTagView(props: { readonly tag: AgentStatusTag }): React.ReactElement {
  return (
    <span className={props.tag.className} style={props.tag.danger ? DANGER_TAG_STYLE : undefined}>
      {props.tag.label}
    </span>
  );
}

export function formatConfigRepoAgentSkillStatus(status: ConfigRepoAgentSkillStatus): string {
  switch (status) {
    case 'ok':
      return 'Ready';
    case 'missing':
      return 'Missing';
    case 'unpublished':
      return 'Unpublished';
  }
}

export function formatConfigRepoAgentMcpStatus(status: ConfigRepoAgentMcpStatus): string {
  switch (status) {
    case 'ok':
      return 'Ready';
    case 'missing':
      return 'Missing';
    case 'invalid':
      return 'Invalid';
  }
}

export function canDeployConfigRepoAgent(status: ConfigRepoAgentStatus): boolean {
  return status === 'new' || status === 'changed' || status === 'unpublished';
}

// fn-8 (AIP-28) C-SPA — a config-repo agent is launchable into a session only
// when it has a deployed platform agent row (`agent_id` present) AND it is not
// `'deleted'` (deployed-then-removed-from-repo agents linger as a DB row but
// must not be re-launched). A repo-only agent (never deployed, `agent_id`
// null) has no platform row for `POST /sessions { agent_id, matter_id }` to
// resolve, so it isn't launchable either.
export function canStartChatWithConfigRepoAgent(agent: {
  readonly agent_id: string | null;
  readonly status: ConfigRepoAgentStatus;
}): agent is { agent_id: string; status: ConfigRepoAgentStatus } {
  return agent.agent_id !== null && agent.status !== 'deleted';
}

/** Human phrasing for why one dependency blocks a deploy. */
function dependencyStatusReason(
  kind: 'Skill' | 'MCP server',
  name: string,
  status: string,
): string {
  const why =
    status === 'no_endpoint'
      ? 'has no endpoint for this environment'
      : status === 'missing'
        ? 'is not in the config repo'
        : status === 'unpublished'
          ? 'is not published yet'
          : status === 'invalid_config'
            ? 'has an invalid config'
            : status === 'scan_failed'
              ? 'failed its last scan'
              : `is "${status}"`;
  return `${kind} "${name}" ${why}`;
}

/**
 * Why Deploy is disabled for this agent, or null when it isn't blocked.
 *
 * A disabled button with no adjacent reason is a dead end — the operator sees
 * a control that does nothing and has no way to learn why (the page-level
 * error banner never fires, because a blocked deploy is never REQUESTED).
 * The failing dependency + its status is already on the row, so say it.
 */
export function deployBlockedReason(
  agent: ConfigRepoAgentWithSkills & ConfigRepoAgentWithMcpServers,
): string | null {
  const reasons: string[] = [];
  for (const skill of agent.skills) {
    // NB: the skill row names its field `skill_name`; the MCP row uses `name`.
    if (skill.status !== 'ok') {
      reasons.push(dependencyStatusReason('Skill', skill.skill_name, skill.status));
    }
  }
  for (const server of agent.mcp_servers) {
    if (server.status !== 'ok') {
      reasons.push(dependencyStatusReason('MCP server', server.name, server.status));
    }
  }
  return reasons.length === 0 ? null : reasons.join('; ');
}

export function configRepoAgentHasSkillDependencyErrors(agent: ConfigRepoAgentWithSkills): boolean {
  return agent.skills.some((skill) => skill.status !== 'ok');
}

export function configRepoAgentHasMcpDependencyErrors(
  agent: ConfigRepoAgentWithMcpServers,
): boolean {
  return agent.mcp_servers.some((server) => server.status !== 'ok');
}

export function formatDependencyErrorDetails(details: DependencyErrorDetailsShape): string[] {
  const lines: string[] = [];
  const missingSkills = details.skills?.missing ?? [];
  const unpublishedSkills = details.skills?.unpublished ?? [];
  const missingMcp = details.mcp_servers?.missing ?? [];
  const invalidMcp = details.mcp_servers?.invalid ?? [];
  const unknownToolMcp = details.tools?.unknown_mcp_server_names ?? [];
  if (missingSkills.length > 0) lines.push(`Missing skills: ${missingSkills.join(', ')}`);
  if (unpublishedSkills.length > 0) {
    lines.push(`Unpublished skills: ${unpublishedSkills.join(', ')}`);
  }
  if (missingMcp.length > 0) lines.push(`Missing MCP servers: ${missingMcp.join(', ')}`);
  if (invalidMcp.length > 0) lines.push(`Invalid MCP servers: ${invalidMcp.join(', ')}`);
  if (unknownToolMcp.length > 0) {
    lines.push(`Tools reference undeclared MCP servers: ${unknownToolMcp.join(', ')}`);
  }
  return lines;
}

export function ConfigRepoAgentActionButton(props: {
  readonly status: ConfigRepoAgentStatus;
  readonly isPending: boolean;
  // Deploy is gated when the agent's skill/MCP dependencies fail
  // validation — the backend would reject the deploy anyway, so we
  // don't advertise a path that cannot succeed.
  readonly isDisabled?: boolean;
  /** WHY it's disabled — rendered beside the button, not just as a title. */
  readonly disabledReason?: string | null;
  /** A failed deploy for THIS agent — shown on the row that was clicked. */
  readonly errorMessage?: string | null;
  readonly onDeploy: () => void;
}): React.ReactElement | null {
  if (!canDeployConfigRepoAgent(props.status)) return null;
  const isDisabled = props.isPending || props.isDisabled === true;
  const reason = props.disabledReason ?? null;
  return (
    <span className="config-repo-deploy">
      <button
        type="button"
        className="btn btn--ghost btn--xs"
        disabled={isDisabled}
        // Native tooltip for the pointer; the visible note below carries the
        // same text for keyboard + screen-reader users.
        {...(reason !== null ? { title: reason } : {})}
        onClick={props.onDeploy}
      >
        {props.isPending ? 'Deploying...' : 'Deploy'}
      </button>
      {reason !== null && (
        <small className="config-repo-deploy__blocked" data-testid="deploy-blocked-reason">
          Can't deploy — {reason}
        </small>
      )}
      {/* The page-level banner sits at the bottom of a long table; a failure
          needs to be visible where the click happened. `<output>` rather than
          `<small role="alert">`: implicit live-region role, valid inline
          content, and the pattern `packages/ui` uses for decision feedback. */}
      {props.errorMessage != null && props.errorMessage.length > 0 && (
        <output className="config-repo-deploy__error" data-testid="deploy-row-error">
          Deploy failed — {props.errorMessage}
        </output>
      )}
    </span>
  );
}

export function DependencyErrorDetails(props: {
  readonly details: DependencyErrorDetailsShape;
}): React.ReactElement | null {
  const lines = formatDependencyErrorDetails(props.details);
  if (lines.length === 0) return null;
  return (
    <ul
      className="agents-list__notice agents-list__notice--error"
      style={{ marginTop: '0.5rem', paddingLeft: '1.1rem' }}
    >
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

// fn-72.3 / R5 — per-row favorite toggle. Prop-driven (no prefs hook) so it
// stays a pure, provider-free testable component; the page wires the live
// `useFavorites()` state in. `aria-pressed` carries the toggle state for AT.
// Console redesign 1a — the glyph is the design-system `star-solid` icon,
// tinted brand-orange when favorited and border-grey otherwise (the fill state
// is the sole colour difference, matching the mock's pinned-row treatment).
export function AgentStar(props: {
  readonly favorited: boolean;
  readonly agentName: string;
  readonly onToggle: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      className={props.favorited ? 'agent-star is-favorited' : 'agent-star'}
      aria-pressed={props.favorited}
      aria-label={
        props.favorited
          ? `Remove ${props.agentName} from favorites`
          : `Add ${props.agentName} to favorites`
      }
      title={props.favorited ? 'Unfavorite' : 'Favorite'}
      onClick={props.onToggle}
    >
      <SbIcon
        name="star-solid"
        size={14}
        style={{
          color: props.favorited ? 'var(--color-tag-bright-orange)' : 'var(--color-border-default)',
        }}
      />
    </button>
  );
}

function formatDate(value: string | null): string {
  if (value === null) return '-';
  return new Date(value).toLocaleString();
}

function shortHash(value: string | null): string {
  if (value === null || value.length === 0) return '-';
  return value.length <= 12 ? value : value.slice(0, 12);
}

function parseDependencyDetailsFromMessage(message: string): DependencyErrorDetailsShape | null {
  const firstBrace = message.indexOf('{');
  if (firstBrace === -1) return null;
  try {
    const parsed = JSON.parse(message.slice(firstBrace)) as { details?: unknown };
    const details = parsed.details;
    if (details !== null && typeof details === 'object') {
      return details as DependencyErrorDetailsShape;
    }
  } catch {
    return null;
  }
  return null;
}

export function readConfigRepoAgentsListInput(params: URLSearchParams): ConfigRepoAgentsListInput {
  const rawLimit = Number(params.get('limit') ?? '10');
  const limit = PAGE_SIZES.includes(rawLimit as (typeof PAGE_SIZES)[number]) ? rawLimit : 10;
  const sortBy = params.get('sort_by');
  const sort_by = CONFIG_REPO_AGENT_SORT_OPTIONS.some((option) => option.value === sortBy)
    ? (sortBy as ConfigRepoAgentSortBy)
    : 'name';
  const sortDir = params.get('sort_dir');
  const sort_dir: SortDirection = sortDir === 'desc' ? 'desc' : 'asc';
  const status = params.get('status');
  const q = normalizedParam(params, 'q');
  const cursor = normalizedParam(params, 'cursor');
  const model_id = normalizedParam(params, 'model_id');
  const agent_ma_id = normalizedParam(params, 'agent_ma_id');
  const skill_name = normalizedParam(params, 'skill_name');
  const mcp_server_name = normalizedParam(params, 'mcp_server_name');
  return {
    limit,
    sort_by,
    sort_dir,
    ...(q !== undefined ? { q } : {}),
    ...(cursor !== undefined ? { cursor } : {}),
    ...(isConfigRepoAgentStatus(status) ? { status } : {}),
    ...(model_id !== undefined ? { model_id } : {}),
    ...(agent_ma_id !== undefined ? { agent_ma_id } : {}),
    ...(skill_name !== undefined ? { skill_name } : {}),
    ...(mcp_server_name !== undefined ? { mcp_server_name } : {}),
  };
}

export function configRepoSectionTargeted(params: URLSearchParams): boolean {
  return (
    params.get('section') === 'config-repo' ||
    normalizedParam(params, 'skill_name') !== undefined ||
    normalizedParam(params, 'mcp_server_name') !== undefined
  );
}

function normalizedParam(params: URLSearchParams, key: string): string | undefined {
  const value = params.get(key)?.trim();
  return value === undefined || value.length === 0 ? undefined : value;
}

function isConfigRepoAgentStatus(value: string | null): value is ConfigRepoAgentStatus {
  return CONFIG_REPO_AGENT_STATUS_OPTIONS.some((option) => option.value === value);
}

/**
 * REPO-SPECIFIC filters: they describe scanned config-repo state (deploy
 * status, model, declared skills/servers) that an orphaned spine row simply
 * doesn't have. A name search is NOT one of them — it's a property of any
 * agent, so orphans are matched on it client-side instead of withheld.
 */
function hasRepoSpecificConfigRepoFilters(input: ConfigRepoAgentsListInput): boolean {
  return (
    input.status !== undefined ||
    input.model_id !== undefined ||
    input.agent_ma_id !== undefined ||
    input.skill_name !== undefined ||
    input.mcp_server_name !== undefined
  );
}

/**
 * Whether the merge may append uncovered `config_repo` spine rows as orphan
 * fallbacks: the UNFILTERED coverage query must have returned the whole repo
 * (success + no further pages) AND no repo-specific filter may be active — an
 * orphan carries no scanned repo state, so it cannot be judged against
 * status/model/skill/server filters and is withheld instead (the merge
 * helper's `configRepoCoversAllRows` contract). A name search (`q`) is
 * deliberately allowed — orphans are name-matched client-side.
 */
export function configRepoCoversAll(
  input: ConfigRepoAgentsListInput,
  coverageQuery: {
    readonly isSuccess: boolean;
    readonly data?: { readonly next_cursor?: string | null } | undefined;
  },
): boolean {
  return (
    coverageQuery.isSuccess &&
    (coverageQuery.data?.next_cursor ?? null) === null &&
    !hasRepoSpecificConfigRepoFilters(input)
  );
}

function hasActiveConfigRepoFilters(input: ConfigRepoAgentsListInput): boolean {
  return (
    input.q !== undefined ||
    input.status !== undefined ||
    input.model_id !== undefined ||
    input.agent_ma_id !== undefined ||
    input.skill_name !== undefined ||
    input.mcp_server_name !== undefined ||
    input.cursor !== undefined
  );
}

export function AgentsListPage(): React.ReactElement {
  const utils = trpc.useUtils();
  const [searchParams, setSearchParams] = useSearchParams();
  const [previousCursors, setPreviousCursors] = useState<string[]>([]);
  const configRepoListInput = readConfigRepoAgentsListInput(searchParams);
  const admin = useIsPlatformAdmin();
  // fn-75 / ADR 0035 / R7 — include-archived toggle (default OFF). When on, the
  // spine query widens to `include_archived=true` so soft-archived console
  // agents surface (rendered greyed with an Unarchive affordance below).
  const [includeArchived, setIncludeArchived] = useState(false);
  // fn-72.3 — the deployed-agent spine. `agents.list` is the resilient source
  // for "which agents exist"; the CP defaults an omitted limit to 50, so fetch
  // at the max page size (200) and surface a truncation notice below if more
  // exist, rather than silently dropping console-source / fallback rows past 50.
  const agentsQuery = trpc.agents.list.useQuery({
    limit: 200,
    ...(includeArchived ? { includeArchived: true } : {}),
  });
  const configRepoAgentsQuery = trpc.configRepoAgents.list.useQuery(configRepoListInput, {
    placeholderData: keepPreviousData,
  });
  // COVERAGE query — deliberately separate from the DISPLAY query above.
  // "Is this spine row covered by the config repo?" is a question about the
  // WHOLE repo, not about the page currently on screen. Inferring it from the
  // display page was wrong twice over: the page is filtered (a search hid the
  // very row being searched for) and paginated (the default page size of 10
  // against 13 repo agents meant orphans were ALWAYS withheld). One unfiltered
  // fetch answers it exactly; the payload is small and it shares the tRPC cache.
  const configRepoCoverageQuery = trpc.configRepoAgents.list.useQuery({
    limit: 200,
    sort_by: 'name',
    sort_dir: 'asc',
  });
  const scanConfigRepoAgents = trpc.configRepoAgents.scan.useMutation();
  const deployConfigRepoAgent = trpc.configRepoAgents.deploy.useMutation();
  const { favorites, isFavorite, toggleFavorite } = useFavorites();

  const setConfigRepoListParam = useCallback(
    (updates: Readonly<Record<string, string | null>>, replace = true): void => {
      const next = new URLSearchParams(searchParams);
      next.set('section', 'config-repo');
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value.trim().length === 0) next.delete(key);
        else next.set(key, value);
      }
      if (!Object.prototype.hasOwnProperty.call(updates, 'cursor')) {
        next.delete('cursor');
        setPreviousCursors([]);
      }
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams],
  );

  const commitSearchFilter = useCallback(
    (value: string): void => setConfigRepoListParam({ q: value }),
    [setConfigRepoListParam],
  );
  const commitModelFilter = useCallback(
    (value: string): void => setConfigRepoListParam({ model_id: value }),
    [setConfigRepoListParam],
  );
  const commitAgentMaIdFilter = useCallback(
    (value: string): void => setConfigRepoListParam({ agent_ma_id: value }),
    [setConfigRepoListParam],
  );
  const [searchDraft, setSearchDraft] = useDebouncedTextFilter(
    configRepoListInput.q,
    commitSearchFilter,
  );
  const [modelDraft, setModelDraft] = useDebouncedTextFilter(
    configRepoListInput.model_id,
    commitModelFilter,
  );
  const [agentMaIdDraft, setAgentMaIdDraft] = useDebouncedTextFilter(
    configRepoListInput.agent_ma_id,
    commitAgentMaIdFilter,
  );

  if (agentsQuery.isPending) {
    return (
      <section className="agents-page" aria-busy="true">
        <div className="page__header">
          <div className="page__title">Agents</div>
        </div>
        <p className="agents-list__notice">Loading agents…</p>
      </section>
    );
  }
  if (agentsQuery.error !== null) {
    return (
      <section className="agents-page" role="alert">
        <div className="page__header">
          <div className="page__title">Agents</div>
        </div>
        <p className="agents-list__notice agents-list__notice--error">
          Failed to load agents: {agentsQuery.error.message}
        </p>
      </section>
    );
  }

  const agents = agentsQuery.data?.data ?? [];
  const agentsTruncated = (agentsQuery.data?.next_cursor ?? null) !== null;
  const configRepoAgents = configRepoAgentsQuery.data?.data ?? [];
  const lastConfigRepoScanAt = configRepoAgentsQuery.data?.last_scan_at ?? null;
  const nextConfigRepoCursor = configRepoAgentsQuery.data?.next_cursor ?? null;
  const isConfigRepoTargeted = configRepoSectionTargeted(searchParams);
  const dependencyDetails =
    deployConfigRepoAgent.error !== null
      ? parseDependencyDetailsFromMessage(deployConfigRepoAgent.error.message)
      : null;

  // fn-72.3 / R4 — the config-repo enrichment query is the ENRICHMENT, not the
  // spine. Distinguish its three outcomes explicitly (memory:
  // `bug/ui/secondary-query-joins-must-branch-on-2026-06-19`): a `data ??
  // fallback` would collapse a failed/pending fetch into a confident empty
  // list. It is "ok" (owns its config_repo rows) ONLY on a settled success.
  // NB: `placeholderData: keepPreviousData` keeps `isPending` false while
  // serving the PREVIOUS filter/page's rows (`isPlaceholderData`), so fold that
  // into "pending" — otherwise `configRepoOk` would render stale rows under the
  // new params (CodeRabbit). Placeholder → the loading branch + fallback rows.
  const configRepoPending =
    configRepoAgentsQuery.isPending || configRepoAgentsQuery.isPlaceholderData;
  const configRepoError = configRepoAgentsQuery.error;
  const configRepoOk = !configRepoPending && configRepoError === null;

  // fn-72.3 / R3-R5 — the single ordered render model. console-source agents
  // (from the resilient `agents.list` spine) + EITHER the rich config-repo
  // rows (success) OR basic `config_repo` fallback rows (degraded), favorites
  // pinned to the top across both groups.
  const agentEntries = buildAgentEntries({
    spineRows: agents,
    configRepoRows: configRepoAgents,
    configRepoOk,
    // Coverage comes from the dedicated unfiltered query — never from the
    // filtered/paginated page on screen.
    coverageRows: configRepoCoverageQuery.data?.data ?? [],
    configRepoCoversAllRows: configRepoCoversAll(configRepoListInput, configRepoCoverageQuery),
    ...(configRepoListInput.q !== undefined ? { nameFilter: configRepoListInput.q } : {}),
    favorites,
  });
  // fn-72.3 — distinguish "no agents exist" from "config-repo view filtered/
  // paged empty" (the spine is unfiltered, so its emptiness is authoritative).
  const emptyState = agentsEmptyState({
    agentEntriesCount: agentEntries.length,
    spineCount: agents.length,
    hasActiveConfigRepoFilters: hasActiveConfigRepoFilters(configRepoListInput),
  });

  const clearConfigRepoCursorForScan = (): void => {
    const next = new URLSearchParams(searchParams);
    next.set('section', 'config-repo');
    next.delete('cursor');
    setPreviousCursors([]);
    setSearchParams(next, { replace: true });
  };

  const agentCount = agentEntries.length;
  const agentCountLabel = `${agentCount} ${agentCount === 1 ? 'agent' : 'agents'}`;
  const hasActiveChips =
    configRepoListInput.status !== undefined ||
    configRepoListInput.model_id !== undefined ||
    configRepoListInput.agent_ma_id !== undefined ||
    configRepoListInput.skill_name !== undefined ||
    configRepoListInput.mcp_server_name !== undefined;

  return (
    <section className="agents-page">
      {/* Page header — title + config-repo scan metadata (caption), then the
          admin-gated include-archived toggle, the config-repo scan action, and
          the admin-gated Add agent primary. */}
      <div className="page__header">
        <div>
          <div className="page__title">Agents</div>
          <div className="caption" style={{ marginTop: '2px' }}>
            Config repo last scanned: {formatDate(lastConfigRepoScanAt)}
          </div>
        </div>
        <div className="page__actions" style={{ alignItems: 'center' }}>
          {/* fn-75 / ADR 0035 / R7 — admin-only include-archived toggle (default
              OFF). Widens the spine query to surface soft-archived console
              agents. Hidden for non-admins (nothing they can do with them). */}
          {admin.isAdmin && (
            <label className="agents-page__archived-toggle">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
              />
              <span>Include archived</span>
            </label>
          )}
          <button
            type="button"
            className="btn btn--tertiary"
            disabled={scanConfigRepoAgents.isPending}
            onClick={() => {
              void (async () => {
                try {
                  await scanConfigRepoAgents.mutateAsync(undefined);
                  clearConfigRepoCursorForScan();
                  await utils.configRepoAgents.list.invalidate();
                } catch {
                  // mutation error state drives the UI error
                }
              })();
            }}
          >
            <SbIcon name="clock-rotate-left" size={16} />
            {scanConfigRepoAgents.isPending ? 'Scanning...' : 'Scan config repo'}
          </button>
          {/* fn-71 / ADR 0035 — admin-only "Add agent" (console DB-backed write
              path). Hidden for non-admins; the control plane is the real gate. */}
          {admin.isAdmin && (
            <Link to="/agents/new" className="btn btn--primary">
              Add agent
            </Link>
          )}
        </div>
      </div>

      <p className="agents-page__intro">
        Create and manage autonomous agents. Console-authored and config-repo agents appear together
        below. Star an agent to pin it to the top; click "Start session" to launch a new session
        against any deployed agent.
      </p>

      {/* Primary filter bar — search + status segmented control + agent count.
          `id="config-repo"` keeps the historical deep-link anchor + the
          `data-targeted` hook (config-repo/skill/mcp deep-links focus here). */}
      <div
        id="config-repo"
        className="filters"
        data-targeted={isConfigRepoTargeted ? 'true' : 'false'}
      >
        <div className="search-input">
          <SbIcon name="search" size={16} />
          <input
            type="search"
            placeholder="Search agents"
            value={searchDraft}
            aria-label="Search config repo agents"
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </div>
        {/* Status segmented control — "All" plus one segment per existing
            config-repo status (the status set the page already distinguishes).
            Each segment drives the SAME `status` URL param the old <select> did,
            so no filter semantics change. `aria-pressed` marks the active one. */}
        {/* biome-ignore lint/a11y/useSemanticElements: a <fieldset> would break the design's .seg flex pill styling; role="group" carries the semantics */}
        <div className="seg" role="group" aria-label="Filter config repo agents by status">
          <button
            type="button"
            className="seg__item"
            aria-pressed={configRepoListInput.status === undefined}
            onClick={() => setConfigRepoListParam({ status: null })}
          >
            All
          </button>
          {CONFIG_REPO_AGENT_STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className="seg__item"
              aria-pressed={configRepoListInput.status === option.value}
              onClick={() => setConfigRepoListParam({ status: option.value })}
            >
              {option.label}
            </button>
          ))}
        </div>
        <span className="agents-list__count">{agentCountLabel}</span>
      </div>

      {/* Secondary (power-user) filters + sort/paging preserved from the prior
          list, kept light so the dense table stays the focus. */}
      <div className="agents-list__subfilters" aria-label="Additional config repo agent filters">
        <label className="agents-list__subfilter">
          <span>Model</span>
          <input
            type="search"
            value={modelDraft}
            aria-label="Filter config repo agents by model"
            onChange={(event) => setModelDraft(event.target.value)}
          />
        </label>
        <label className="agents-list__subfilter">
          <span>MA ID</span>
          <input
            type="search"
            value={agentMaIdDraft}
            aria-label="Filter config repo agents by Managed Agent ID"
            onChange={(event) => setAgentMaIdDraft(event.target.value)}
          />
        </label>
        <label className="agents-list__subfilter">
          <span>Sort</span>
          <select
            value={configRepoListInput.sort_by}
            aria-label="Sort config repo agents by"
            onChange={(event) => setConfigRepoListParam({ sort_by: event.target.value })}
          >
            {CONFIG_REPO_AGENT_SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="agents-list__subfilter">
          <span>Direction</span>
          <select
            value={configRepoListInput.sort_dir}
            aria-label="Sort config repo agents direction"
            onChange={(event) => setConfigRepoListParam({ sort_dir: event.target.value })}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </label>
        <label className="agents-list__subfilter">
          <span>Page size</span>
          <select
            value={String(configRepoListInput.limit)}
            aria-label="Config repo agents page size"
            onChange={(event) => setConfigRepoListParam({ limit: event.target.value })}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      {hasActiveChips && (
        <div className="agents-list__chips" aria-label="Active config repo agent filters">
          {configRepoListInput.status !== undefined && (
            <button
              type="button"
              className="filter-chip"
              onClick={() => setConfigRepoListParam({ status: null })}
            >
              Status: {formatConfigRepoAgentStatus(configRepoListInput.status)}
              <SbIcon name="xmark" size={12} />
            </button>
          )}
          {configRepoListInput.model_id !== undefined && (
            <button
              type="button"
              className="filter-chip"
              onClick={() => setConfigRepoListParam({ model_id: null })}
            >
              Model: {configRepoListInput.model_id}
              <SbIcon name="xmark" size={12} />
            </button>
          )}
          {configRepoListInput.agent_ma_id !== undefined && (
            <button
              type="button"
              className="filter-chip"
              onClick={() => setConfigRepoListParam({ agent_ma_id: null })}
            >
              MA ID: {configRepoListInput.agent_ma_id}
              <SbIcon name="xmark" size={12} />
            </button>
          )}
          {configRepoListInput.skill_name !== undefined && (
            <button
              type="button"
              className="filter-chip"
              onClick={() => setConfigRepoListParam({ skill_name: null })}
            >
              Skill: {configRepoListInput.skill_name}
              <SbIcon name="xmark" size={12} />
            </button>
          )}
          {configRepoListInput.mcp_server_name !== undefined && (
            <button
              type="button"
              className="filter-chip"
              onClick={() => setConfigRepoListParam({ mcp_server_name: null })}
            >
              MCP: {configRepoListInput.mcp_server_name}
              <SbIcon name="xmark" size={12} />
            </button>
          )}
        </div>
      )}

      {/* fn-72.3 / R4 — config-repo enrichment status, surfaced INLINE so a
          failed/pending secondary fetch never collapses into a silent empty
          list. The deployed config_repo agents still render below as basic
          fallback rows while this is unavailable. */}
      {configRepoPending && (
        <p className="agents-list__notice" aria-busy="true">
          Loading config repo metadata… deployed config-repo agents are shown below with limited
          controls until it loads.
        </p>
      )}
      {configRepoError !== null && (
        <p role="alert" className="agents-list__notice agents-list__notice--error">
          Config repo metadata unavailable: {configRepoError.message}. Deployed config-repo agents
          are still shown below with limited controls.
        </p>
      )}
      {configRepoOk && configRepoAgents.length === 0 && agentEntries.length > 0 && (
        <p className="agents-list__notice">
          {hasActiveConfigRepoFilters(configRepoListInput)
            ? 'No config repo agents match current filters.'
            : 'No config repo agents scanned yet.'}
        </p>
      )}

      {agentsTruncated && (
        <p className="agents-list__notice" role="note">
          Showing the first 200 agents — more exist. Use the platform API to list them all.
        </p>
      )}

      {agentEntries.length === 0 ? (
        <p className="agents-list__empty">
          {emptyState === 'no-agents'
            ? 'No agents available. Ask a firm admin to publish one.'
            : 'No agents match the current config repo filters.'}
        </p>
      ) : (
        <>
          <div className="agents-list__table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>Agent</th>
                  <th>Status</th>
                  <th>Skills</th>
                  <th>Model</th>
                  <th>Repo</th>
                  <th>Updated</th>
                  <th aria-label="Row actions" />
                </tr>
              </thead>
              <tbody>
                {agentEntries.map((entry) => {
                  if (entry.kind === 'config-repo') {
                    const agent = entry.agentRow;
                    return (
                      <ConfigRepoAgentRow
                        key={entry.favoriteKey}
                        agent={agent}
                        {...(entry.memory !== undefined ? { memory: entry.memory } : {})}
                        favorited={isFavorite(entry.favoriteKey)}
                        onToggleFavorite={() => toggleFavorite(entry.favoriteKey)}
                        deployIsPending={
                          deployConfigRepoAgent.isPending &&
                          deployConfigRepoAgent.variables?.name === agent.name
                        }
                        deployIsDisabled={
                          deployConfigRepoAgent.isPending ||
                          configRepoAgentHasSkillDependencyErrors(agent) ||
                          configRepoAgentHasMcpDependencyErrors(agent)
                        }
                        deployBlockedReason={deployBlockedReason(agent)}
                        deployErrorMessage={
                          deployConfigRepoAgent.error !== null &&
                          deployConfigRepoAgent.variables?.name === agent.name
                            ? deployConfigRepoAgent.error.message
                            : null
                        }
                        onDeploy={() => {
                          void (async () => {
                            try {
                              await deployConfigRepoAgent.mutateAsync({ name: agent.name });
                              await Promise.all([
                                utils.configRepoAgents.list.invalidate(),
                                utils.agents.list.invalidate(),
                              ]);
                            } catch {
                              // mutation error state drives the UI error
                            }
                          })();
                        }}
                        onFilterSkill={(skillName) =>
                          setConfigRepoListParam({ skill_name: skillName })
                        }
                      />
                    );
                  }
                  // console + config-repo-fallback — both are deployed (have an
                  // id), so both render the same basic row (name link + start +
                  // star). fn-75 — only a console entry can be archived
                  // (config-repo agents are never archivable); an archived row
                  // is greyed + swaps Start for an Unarchive action.
                  const archived = entry.kind === 'console' && entry.archived;
                  return (
                    <BasicAgentRow
                      key={entry.favoriteKey}
                      agentId={entry.id}
                      name={entry.name}
                      source={entry.kind === 'console' ? 'console' : 'config_repo'}
                      archived={archived}
                      {...(entry.memory !== undefined ? { memory: entry.memory } : {})}
                      favorited={isFavorite(entry.favoriteKey)}
                      onToggleFavorite={() => toggleFavorite(entry.favoriteKey)}
                      onUnarchived={() => {
                        void Promise.all([
                          utils.agents.list.invalidate(),
                          utils.configRepoAgents.list.invalidate(),
                        ]);
                      }}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Cursor-based config-repo pagination, styled as the design's
              standalone bordered pagination footer (tucked under the table). */}
          <div
            className="pagination"
            style={{
              border: '1px solid var(--color-border-default)',
              borderRadius: 'var(--radius-md)',
              marginTop: '-8px',
            }}
            aria-label="Config repo agents pagination"
          >
            <span className="info">Showing {agentCountLabel}</span>
            <button
              type="button"
              className="page-btn"
              aria-label="Previous page"
              disabled={previousCursors.length === 0}
              onClick={() => {
                const previous = previousCursors[previousCursors.length - 1] ?? '';
                setPreviousCursors((stack) => stack.slice(0, -1));
                setConfigRepoListParam({ cursor: previous.length > 0 ? previous : null }, false);
              }}
            >
              <SbIcon name="chevron-down" size={14} style={{ transform: 'rotate(90deg)' }} />
            </button>
            <button
              type="button"
              className="page-btn"
              aria-label="Next page"
              disabled={nextConfigRepoCursor === null}
              onClick={() => {
                if (nextConfigRepoCursor === null) return;
                setPreviousCursors((stack) => [...stack, configRepoListInput.cursor ?? '']);
                setConfigRepoListParam({ cursor: nextConfigRepoCursor }, false);
              }}
            >
              <SbIcon name="chevron-right" size={14} />
            </button>
          </div>
        </>
      )}

      {scanConfigRepoAgents.data !== undefined && (
        <p className="agents-list__notice">
          Scan found {scanConfigRepoAgents.data.discovered_count} agents;{' '}
          {scanConfigRepoAgents.data.valid_count} valid, {scanConfigRepoAgents.data.invalid_count}{' '}
          invalid.
        </p>
      )}
      {scanConfigRepoAgents.error !== null && (
        <p role="alert" className="agents-list__notice agents-list__notice--error">
          Failed to scan config repo agents: {scanConfigRepoAgents.error.message}
        </p>
      )}
      {deployConfigRepoAgent.error !== null && (
        <div role="alert">
          <p className="agents-list__notice agents-list__notice--error">
            Failed to deploy config repo agent: {deployConfigRepoAgent.error.message}
          </p>
          {dependencyDetails !== null && <DependencyErrorDetails details={dependencyDetails} />}
        </div>
      )}
    </section>
  );
}

// fn-72.3 / R3-R5 — a basic agent ROW for a DEPLOYED agent sourced from the
// `agents.list` spine: a console-authored agent (`source='console'`) OR a
// config_repo agent rendered in the degraded fallback (config-repo enrichment
// query unavailable). Both always have a platform agent id, so both are
// clickable + startable + favoriteable. The spine carries no status / skills /
// model / repo / updated metadata (that lives on the config-repo query), so
// those cells render as em-dashes; a live deployed row is tagged "Deployed"
// (or "Archived" for a soft-archived console agent). Rich config-repo controls
// (deploy, dependency gating, scan metadata) live on `ConfigRepoAgentRow`.
function BasicAgentRow(props: {
  readonly agentId: string;
  readonly name: string;
  readonly source: 'console' | 'config_repo';
  // fn-75 — an archived console agent: greyed, no Start affordance, Unarchive.
  readonly archived?: boolean;
  readonly favorited: boolean;
  readonly onToggleFavorite: () => void;
  readonly onUnarchived?: () => void;
  // ADR 0043 — the agent's declared memory map (read-only badge).
  readonly memory?: AgentMemoryStatus;
}): React.ReactElement {
  const archived = props.archived === true;
  // The spine has no description; convey the row's source in the caption slot so
  // the dual-source distinction the old list showed as a chip is preserved.
  const caption = props.source === 'console' ? 'Console agent' : 'Config repo agent';
  return (
    <tr className={archived ? 'agents-list__row--archived' : undefined}>
      <td>
        <div className="matter">
          <Link
            to={`/agents/${encodeURIComponent(props.agentId)}`}
            className="name agents-list__name"
          >
            {props.name}
          </Link>
          <span className="caption">{caption}</span>
          <MemoryStatusBadge memory={props.memory} />
        </div>
      </td>
      <td>
        <StatusTagView tag={deployedStatusTag(archived)} />
      </td>
      <td className="agents-list__skills">—</td>
      <td className="agents-list__model">—</td>
      <td>
        <span className="agents-list__sha">—</span>
      </td>
      <td className="agents-list__muted">—</td>
      <td>
        <div className="agents-list__row-actions">
          {archived ? (
            <AgentUnarchiveButton
              agentId={props.agentId}
              agentName={props.name}
              {...(props.onUnarchived !== undefined ? { onUnarchived: props.onUnarchived } : {})}
            />
          ) : (
            <ConfigRepoAgentStartChat
              agentId={props.agentId}
              agentName={props.name}
              // fn-2.2 — keep the inline context-document paste for console-
              // authored agents (the old top table carried it; consolidation
              // preserves it rather than dropping the affordance).
              enableContextDocument={props.source === 'console'}
            />
          )}
          <AgentStar
            favorited={props.favorited}
            agentName={props.name}
            onToggle={props.onToggleFavorite}
          />
        </div>
      </td>
    </tr>
  );
}

// fn-75 / ADR 0035 / R7 — inline Unarchive control for an archived console
// agent in the list. Two-step confirm; wires `agents.unarchive` through the
// BFF-origin vanilla tRPC client (split-origin safe, never a bare fetch). On
// success it invalidates the list so the row re-renders active (the caller
// passes the invalidation via `onUnarchived`). A 409 (the freed name is now
// held by another active agent) surfaces inline.
function AgentUnarchiveButton(props: {
  readonly agentId: string;
  readonly agentName: string;
  readonly onUnarchived?: () => void;
}): React.ReactElement {
  const [confirming, setConfirming] = useState(false);
  const unarchive = trpc.agents.unarchive.useMutation();
  const run = (): void => {
    void (async () => {
      try {
        await unarchive.mutateAsync({ agentId: props.agentId });
        setConfirming(false);
        props.onUnarchived?.();
      } catch {
        // The mutation error state drives the inline message below.
      }
    })();
  };
  return (
    <span className="agents-list__unarchive">
      {confirming ? (
        <>
          <button
            type="button"
            className="btn btn--secondary btn--xs"
            disabled={unarchive.isPending}
            onClick={run}
          >
            {unarchive.isPending ? 'Unarchiving…' : 'Confirm unarchive'}
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            disabled={unarchive.isPending}
            onClick={() => setConfirming(false)}
          >
            Cancel
          </button>
        </>
      ) : (
        <button
          type="button"
          className="btn btn--ghost btn--xs"
          onClick={() => setConfirming(true)}
        >
          Unarchive
        </button>
      )}
      {unarchive.error !== null && (
        <span className="agents-list__unarchive-error" role="alert">
          {unarchive.error.message}
        </span>
      )}
    </span>
  );
}

// fn-72.3 / R3-R5 — the rich config-repo ROW (success path). Carries the deploy
// control (+ dependency gating), the start-session flow, and the favorite star,
// with the config-repo metadata mapped onto the dense columns (status tag,
// skills text, model, repo sha, updated). MCP-server / MA-id / MA-version /
// deployed-hash detail moved to the agent detail page for scan density (the MCP
// dependency state still gates Deploy via `deployIsDisabled`). Prop-driven so
// the page wires the deploy mutation + skill filter + favorites state in.
function ConfigRepoAgentRow(props: {
  readonly agent: ConfigRepoAgentRow;
  readonly favorited: boolean;
  readonly onToggleFavorite: () => void;
  readonly deployIsPending: boolean;
  readonly deployIsDisabled: boolean;
  /** Why Deploy is blocked (null when it isn't) — shown beside the button. */
  readonly deployBlockedReason?: string | null;
  /** A failed deploy for THIS agent — shown beside the button. */
  readonly deployErrorMessage?: string | null;
  readonly onDeploy: () => void;
  readonly onFilterSkill: (skillName: string) => void;
  // ADR 0043 — the agent's declared memory map, joined from the spine (the
  // config-repo projection doesn't carry it). Read-only badge.
  readonly memory?: AgentMemoryStatus;
}): React.ReactElement {
  const { agent } = props;
  return (
    <tr>
      <td>
        <div className="matter">
          {/* fn-52.3 / R3 — link deployed agents to the details view; repo-only
              (never-deployed) agents have no platform row, so render plain (and
              in the default text colour, not the brand link colour). */}
          {agent.agent_id !== null ? (
            <Link
              to={`/agents/${encodeURIComponent(agent.agent_id)}`}
              className="name agents-list__name"
            >
              {agent.name}
            </Link>
          ) : (
            <span className="name" style={{ color: 'var(--fg)' }}>
              {agent.name}
            </span>
          )}
          <span className="caption">{agent.description ?? 'No description'}</span>
          {agent.scan_error !== null && (
            <span className="caption agents-list__notice--error">{agent.scan_error}</span>
          )}
          <MemoryStatusBadge memory={props.memory} />
        </div>
      </td>
      <td>
        <StatusTagView tag={configRepoAgentStatusTag(agent.status)} />
      </td>
      <td className="agents-list__skills">
        {agent.skills.length === 0
          ? '—'
          : agent.skills.map((skill, index) => (
              <Fragment key={skill.skill_name}>
                {index > 0 && ', '}
                <button
                  type="button"
                  className={
                    skill.status === 'ok'
                      ? 'agents-list__skill-btn'
                      : 'agents-list__skill-btn agents-list__skill-btn--error'
                  }
                  title={
                    skill.status === 'ok'
                      ? `Filter by skill ${skill.skill_name}`
                      : `${skill.skill_name}: ${formatConfigRepoAgentSkillStatus(skill.status)}`
                  }
                  onClick={() => props.onFilterSkill(skill.skill_name)}
                >
                  {skill.skill_name}
                </button>
              </Fragment>
            ))}
      </td>
      <td className="agents-list__model">{agent.model_id ?? '—'}</td>
      <td>
        <span className="agents-list__sha">{shortHash(agent.repo_blob_hash)}</span>
      </td>
      <td className="agents-list__muted">{formatDate(agent.updated_at)}</td>
      <td>
        <div className="agents-list__row-actions">
          {canStartChatWithConfigRepoAgent(agent) && (
            <ConfigRepoAgentStartChat agentId={agent.agent_id} agentName={agent.name} />
          )}
          <ConfigRepoAgentActionButton
            status={agent.status}
            isPending={props.deployIsPending}
            isDisabled={props.deployIsDisabled}
            disabledReason={props.deployBlockedReason ?? null}
            errorMessage={props.deployErrorMessage ?? null}
            onDeploy={props.onDeploy}
          />
          <AgentStar
            favorited={props.favorited}
            agentName={agent.name}
            onToggle={props.onToggleFavorite}
          />
        </div>
      </td>
    </tr>
  );
}

// fn-8 (AIP-28) C-SPA — per-config-repo-agent "start chat". Reuses the same
// session-launch path as the runnable Agents table (`runs.start` →
// `POST /sessions { agent_id, matter_id }` → navigate to `/runs/:id`). Matter
// id is free-text + optional: the BFF synthesizes a firm-level matter when
// omitted, matching the runnable table's behavior and the wire contract
// (matter_id is an opaque non-empty string, not a strict UUID). Rendered only
// for launchable agents (deployed, non-`deleted`); the section still lists
// `deleted` agents but without a start affordance.
export function ConfigRepoAgentStartChat(props: {
  readonly agentId: string;
  readonly agentName: string;
  // fn-2.2 / fn-72.3 — optional inline context-document paste (a CLAUDE.md /
  // briefing the CP fires as the bootstrap user message). Off by default to
  // keep config-repo rows compact; the consolidated Agents list enables it for
  // console-authored rows (which carried it on the old top table).
  readonly enableContextDocument?: boolean;
}): React.ReactElement {
  const [matterId, setMatterId] = useState('');
  const [contextDocument, setContextDocument] = useState('');
  const [contextOpen, setContextOpen] = useState(false);
  // ADR 0043 — memory attachment is defined by the agent, not chosen at start,
  // so there is no per-scope memory picker here.
  const navigate = useNavigate();
  const startMutation = trpc.runs.start.useMutation();
  const trackUserAction = useTrackUserAction();
  const otel = useClientOtel();
  const enableContextDocument = props.enableContextDocument === true;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const normalizedMatterId = matterId.trim();
    const normalizedContext = enableContextDocument ? contextDocument.trim() : '';
    try {
      trackUserAction({
        name: 'spa.runs.start.click',
        metadata: {
          agent_id: props.agentId,
          source: 'config-repo-agents',
          ...(normalizedMatterId.length > 0 ? { matter_id: normalizedMatterId } : {}),
        },
      });
    } catch {
      // Telemetry is best-effort and must not block session creation.
    }
    try {
      const res = await startMutation.mutateAsync({
        agentId: props.agentId,
        ...(normalizedMatterId.length > 0 ? { matterId: normalizedMatterId } : {}),
        ...(normalizedContext.length > 0 ? { contextDocument } : {}),
      });
      try {
        if (otel !== null) {
          await otel.primeSessionTrace(res.id);
        }
        trackUserAction({
          name: 'spa.runs.start.created',
          sessionId: res.id,
          metadata: {
            agent_id: props.agentId,
            source: 'config-repo-agents',
            session_id: res.id,
            ...(normalizedMatterId.length > 0 ? { matter_id: normalizedMatterId } : {}),
          },
        });
      } catch {
        // Telemetry is best-effort and must not block navigation.
      }
      navigate(`/runs/${encodeURIComponent(res.id)}`);
    } catch {
      // The mutation surfaces error state via `startMutation.error`.
    }
  };

  const matterInputId = `config-repo-matter-${props.agentId}`;
  return (
    <form className="agents-list__start" onSubmit={submit}>
      <label className="visually-hidden" htmlFor={matterInputId}>
        Matter id for {props.agentName}
      </label>
      <input
        id={matterInputId}
        className="agents-list__start-matter"
        type="text"
        placeholder="Matter id (optional)"
        value={matterId}
        onChange={(e) => setMatterId(e.target.value)}
        disabled={startMutation.isPending}
      />
      <button type="submit" disabled={startMutation.isPending} className="btn btn--ghost btn--xs">
        {startMutation.isPending ? 'Starting…' : 'Start session'}
      </button>
      {enableContextDocument && (
        <div className="agents-list__start-context">
          <button
            type="button"
            className="agents-list__context-toggle"
            onClick={() => setContextOpen((v) => !v)}
          >
            {contextOpen ? '▾' : '▸'} Paste context (sent as the first thread message)
            {contextDocument.trim().length > 0 && !contextOpen && (
              <span className="agents-list__context-count">· {contextDocument.length} chars</span>
            )}
          </button>
          <label className="visually-hidden" htmlFor={`context-${props.agentId}`}>
            Context document for new session
          </label>
          {contextOpen && (
            <textarea
              id={`context-${props.agentId}`}
              className="agents-list__context-input"
              value={contextDocument}
              onChange={(e) => setContextDocument(e.target.value)}
              placeholder="Paste a CLAUDE.md / briefing here. The agent sees this as the first message; you'll see it in the thread too."
              rows={4}
              disabled={startMutation.isPending}
            />
          )}
        </div>
      )}
      {startMutation.error !== null && (
        <p className="agents-list__start-error" role="alert">
          {startMutation.error.message}
        </p>
      )}
    </form>
  );
}
